/**
 * The pinned goal in the Wire (M10 P5a2b, plan 2026-10-17-m10-p5a §3.4 and review change F6) and the HUD goal chip's
 * host (P5a2a's hand-off, D-1088).
 *
 * - **Wire lines:** one line when a hull is pinned ("Goal pinned: Danube; 2 steps left") and one when each of its gate
 *   steps is reached ("Danube: Meridian standing 25 reached; 1 step left"). They go to the worker's Wire through the
 *   existing `postWire` command (kind `notice`; there is no main-thread Wire push, review F6).
 * - **Posted once:** each line's key (`pinned`, or the step's `goalStepKey`) is written to `Career.pinnedHull.posted`
 *   (P5a0's key) **before** the line is sent, so a reload, or the same Career seen again, never re-posts. The steps
 *   already met when the hull is pinned are recorded silently with the pin line: only a step that clears later posts.
 *   A step that is lost and met again doesn't post twice.
 * - **The chip:** the host mounts P5a2a's `mountGoalChip` lazily, the first time there is a goal to show, and calls
 *   `writeGoal(view, goalModel(career, { corporation, ownerOf }))` on every Career change (a new Career reference;
 *   Careers are immutable). The company and the yard owner come from the worker (`fleetView`, `stationView`), read
 *   only while a pin exists and the gates are on.
 * - **Keys off:** while `shipyard.json enabled` is false no pin can be made and `goalWireStep` is null; the host
 *   reads nothing from the worker, mounts no chip, writes no Career and posts nothing, so today's game is unchanged.
 */
import { logger } from '../../core/logger'
import type { Career } from '../../career/types'
import { goalModel, goalStepKey, writeGoal, type GoalContext, type GoalView } from '../../hud/goal'
import { gateHullOf, gateOf, gatePlayerOf, gateStatus, shipyardRules, type GateStep, type ShipyardRules } from '../../ui/fleet/models/gates'
import { nameOfFaction } from '../../ui/fleet/models/shipyard'

/** The `posted` key of the pin line. */
export const PIN_POSTED_KEY = 'pinned'

/** The Wire severity of a goal line (a quiet notice). */
export const GOAL_WIRE_SEVERITY = 1

/** The gate steps in gate order (`gateStatus`'s order). */
const STEP_ORDER: readonly GateStep['kind'][] = ['leaseOwned', 'corporation', 'makerStanding', 'armsLicence']

export interface GoalWireStep {
  /** The Wire headlines to post, in order. */
  headlines: string[]
  /** The pin's `posted` list once they are posted. */
  posted: string[]
}

function stepsLeft(n: number): string {
  if (n === 0) return 'every step met: buy it at the yard'
  return n === 1 ? '1 step left' : `${n} steps left`
}

function reachedLine(kind: GateStep['kind'], standingFaction: string | null, need: number): string {
  switch (kind) {
    case 'leaseOwned':
      return 'lease owned'
    case 'corporation':
      return 'corporation founded'
    case 'armsLicence':
      return 'Charter arms licence held'
    case 'makerStanding':
      return `${standingFaction === null ? 'standing' : `${nameOfFaction(standingFaction)} standing`} ${Math.round(need)} reached`
  }
}

/**
 * The goal lines the Career owes the Wire now, or null when it owes none (no pin, the gates off, not a player hull,
 * or every line already posted).
 */
export function goalWireStep(c: Pick<Career, 'pinnedHull' | 'lease' | 'standings'>, ctx: GoalContext, rules: ShipyardRules = shipyardRules()): GoalWireStep | null {
  const pin = c.pinnedHull
  if (pin === undefined || !rules.enabled) return null
  const row = gateHullOf(pin.hull)
  const goal = goalModel(c, ctx, rules)
  if (row === null || goal === null) return null
  const player = gatePlayerOf(c, ctx.corporation)
  const owner = ctx.ownerOf(pin.stationId)
  const st = gateStatus(player, row, { owner, standing: owner === null ? 0 : player.standingOf(owner) }, rules)
  const { steps } = gateOf(row, rules)
  const missing = new Set(st.missing.map(goalStepKey))
  const required = STEP_ORDER.filter((k) => (k === 'makerStanding' ? steps.makerStanding !== undefined : steps[k] === true))
  const posted = pin.posted ?? []
  const next = [...posted]
  const headlines: string[] = []
  const first = !posted.includes(PIN_POSTED_KEY)
  for (const k of required) {
    if (missing.has(k) || next.includes(k)) continue
    next.push(k)
    // The steps already met when the hull is pinned go into `posted` silently.
    if (!first) headlines.push(`${goal.name}: ${reachedLine(k, st.standingFaction, steps.makerStanding ?? 0)}; ${stepsLeft(missing.size)}`)
  }
  if (first) {
    next.push(PIN_POSTED_KEY)
    headlines.unshift(`Goal pinned: ${goal.name}; ${stepsLeft(missing.size)}`)
  }
  return headlines.length === 0 ? null : { headlines, posted: next }
}

/** `c` with the pin's `posted` list set, when the pin is still `hull` at `stationId` (else `c` as is). */
export function withPosted(c: Career, hull: string, stationId: string, posted: string[]): Career {
  const p = c.pinnedHull
  if (p === undefined || p.hull !== hull || p.stationId !== stationId) return c
  return { ...c, pinnedHull: { ...p, posted } }
}

/** The worker command a goal line goes out as. */
export interface GoalWireCommand {
  type: 'postWire'
  item: { headline: string; severity: number; systemId?: string }
}

/** What the host reads of the pinned yard from the worker. */
export interface GoalYardInfo {
  /** A founded company (not a sole trader). */
  corporation: boolean
  /** The yard station's strategic owner (null: not known). */
  owner: string | null
  /** The yard's system (the Wire line's system; null: the worker's default). */
  systemId: string | null
}

export interface GoalHostDeps {
  career(): Career
  /** Applies a Career change (the runtime's `applyDevCommand`); `ok: false` = refused, nothing is posted. */
  apply(name: string, fn: (c: Career) => Career): { ok: boolean }
  /** The pinned yard's facts from the worker. */
  yard(stationId: string): Promise<GoalYardInfo>
  /** Sends a goal line to the worker's Wire. */
  post(cmd: GoalWireCommand): Promise<unknown>
  /** Mounts the HUD chip (called once, the first time a goal shows). */
  mount(): GoalView
  rules?: () => ShipyardRules
}

export interface GoalHost {
  /** Call with the current Career (each frame is fine: only a new Career reference does any work). */
  careerChanged(c: Career): void
  /** Resolves when the read in flight (if any) has been handled (tests). */
  settle(): Promise<void>
  /** The chip, once mounted. */
  readonly view: GoalView | null
  dispose(): void
}

export function createGoalHost(deps: GoalHostDeps): GoalHost {
  const rulesOf = deps.rules ?? shipyardRules
  let last: Career | null = null
  let view: GoalView | null = null
  let seq = 0
  let inFlight: Promise<void> = Promise.resolve()
  let disposed = false

  function show(c: Career, ctx: GoalContext, rules: ShipyardRules): void {
    const m = goalModel(c, ctx, rules)
    if (m === null && view === null) return
    view ??= deps.mount()
    writeGoal(view, m)
  }

  function handle(s: number, stationId: string, info: GoalYardInfo, rules: ShipyardRules): void {
    if (disposed || s !== seq) return
    const c = deps.career()
    const pin = c.pinnedHull
    if (pin === undefined || pin.stationId !== stationId) return
    const ctx: GoalContext = { corporation: info.corporation, ownerOf: (id) => (id === stationId ? info.owner : null) }
    show(c, ctx, rules)
    const step = goalWireStep(c, ctx, rules)
    if (step === null) return
    // `posted` first: a refused write posts nothing, and a line is never sent twice.
    const r = deps.apply('pinned goal wire', (cur) => withPosted(cur, pin.hull, pin.stationId, step.posted))
    if (!r.ok) return
    for (const headline of step.headlines) {
      const item = info.systemId === null ? { headline, severity: GOAL_WIRE_SEVERITY } : { headline, severity: GOAL_WIRE_SEVERITY, systemId: info.systemId }
      deps.post({ type: 'postWire', item }).catch((err: unknown) => logger.warn('Reach Wire: a pinned-goal line was not posted', { err: String(err) }))
    }
  }

  return {
    careerChanged(c: Career): void {
      if (disposed || c === last) return
      last = c
      const rules = rulesOf()
      const pin = c.pinnedHull
      if (!rules.enabled || pin === undefined) {
        seq++
        if (view !== null) writeGoal(view, null)
        return
      }
      const s = ++seq
      const stationId = pin.stationId
      inFlight = deps.yard(stationId).then(
        (info) => handle(s, stationId, info, rules),
        (err: unknown) => logger.warn('Reach Wire: the pinned yard could not be read', { stationId, err: String(err) }),
      )
    },
    settle(): Promise<void> {
      return inFlight
    },
    get view(): GoalView | null {
      return view
    },
    dispose(): void {
      disposed = true
      seq++
      if (view !== null) writeGoal(view, null)
    },
  }
}
